import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { workOrdersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const { isAdminOrTis } = useAuth();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await workOrdersAPI.getDashboard();
      setData(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percent, remainingDays) => {
    if (remainingDays < 3) return 'bg-red-500';
    if (remainingDays < 7) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Devam Eden İşler</p>
              <p className="text-3xl font-bold text-blue-600">{data?.stats?.totalActive || 0}</p>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Tamamlanan İşler</p>
              <p className="text-3xl font-bold text-green-600">{data?.stats?.totalCompleted || 0}</p>
            </div>
            <div className="text-4xl">✅</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Aktif Firmalar</p>
              <p className="text-3xl font-bold text-purple-600">{data?.stats?.totalCompanies || 0}</p>
            </div>
            <div className="text-4xl">🏢</div>
          </div>
        </div>
      </div>

      {/* Butonlar */}
      <div className="flex space-x-4">
        {isAdminOrTis() && (
          <button
            onClick={() => setShowAssignModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Fason İmalat Ata
          </button>
        )}
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          className={`px-6 py-3 rounded-lg transition-colors font-medium ${
            showCompleted ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {showCompleted ? 'Devam Edenleri Göster' : 'Biten İşleri Göster'}
        </button>
      </div>

      {/* İş Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(showCompleted ? data?.completedOrders : data?.activeOrders)?.map((order) => (
          <Link
            key={order.id}
            to={`/work-orders/${order.id}`}
            className="bg-white rounded-xl shadow hover:shadow-lg transition-shadow p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-lg text-gray-800">{order.jobNumber}</h3>
                <p className="text-gray-500 text-sm">{order.mainCompany}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                order.status === 'completed' ? 'bg-green-100 text-green-700' :
                order.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {order.status === 'completed' ? 'Tamamlandı' :
                 order.status === 'in_progress' ? 'Devam Ediyor' : 'Bekliyor'}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-500 mb-1">
                <span>İlerleme</span>
                <span>%{order.progressPercent}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${getProgressColor(order.progressPercent, order.remainingDays)}`}
                  style={{ width: `${order.progressPercent}%` }}
                ></div>
              </div>
            </div>

            {/* Alt Bilgiler */}
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-gray-500">Teslim</p>
                <p className="font-medium">{new Date(order.deliveryDate).toLocaleDateString('tr-TR')}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500">Kalan</p>
                <p className={`font-medium ${order.remainingDays < 3 ? 'text-red-600' : order.remainingDays < 7 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {order.remainingDays > 0 ? `${order.remainingDays} gün` : 'Gecikmiş'}
                </p>
              </div>
            </div>

            {/* Atanan Firmalar */}
            {order.Assignments?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500 mb-2">Atanan Firmalar:</p>
                <div className="flex flex-wrap gap-2">
                  {order.Assignments.map((a) => (
                    <span key={a.id} className="px-2 py-1 bg-gray-100 rounded text-xs">
                      {a.Company?.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Link>
        ))}
      </div>

      {/* Atama Modal */}
      {showAssignModal && (
        <AssignmentModal onClose={() => setShowAssignModal(false)} onRefresh={fetchDashboard} />
      )}
    </div>
  );
};

// Atama Modal Bileşeni
const AssignmentModal = ({ onClose, onRefresh }) => {
  const [assignmentData, setAssignmentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignmentStatus();
  }, []);

  const fetchAssignmentStatus = async () => {
    try {
      const response = await workOrdersAPI.getAssignmentStatus();
      setAssignmentData(response.data);
    } catch (error) {
      console.error('Assignment status error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Fason İmalat Ata</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            &times;
          </button>
        </div>

        <div className="p-6 overflow-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              {/* Atamasız İşler */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-yellow-600">Ataması Olmayan İşler</h3>
                <div className="space-y-3">
                  {assignmentData?.unassigned?.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Ataması olmayan iş yok</p>
                  ) : (
                    assignmentData?.unassigned?.map((order) => (
                      <Link
                        key={order.id}
                        to={`/work-orders/${order.id}`}
                        onClick={onClose}
                        className="block p-4 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100"
                      >
                        <p className="font-medium">{order.jobNumber}</p>
                        <p className="text-sm text-gray-500">{order.mainCompany}</p>
                      </Link>
                    ))
                  )}
                </div>
              </div>

              {/* Atamalı İşler */}
              <div>
                <h3 className="font-bold text-lg mb-4 text-green-600">Ataması Olan İşler</h3>
                <div className="space-y-3">
                  {assignmentData?.assigned?.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Ataması olan iş yok</p>
                  ) : (
                    assignmentData?.assigned?.map((order) => (
                      <Link
                        key={order.id}
                        to={`/work-orders/${order.id}`}
                        onClick={onClose}
                        className="block p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
                      >
                        <p className="font-medium">{order.jobNumber}</p>
                        <p className="text-sm text-gray-500">{order.mainCompany}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {order.Assignments?.map((a) => (
                            <span key={a.id} className="px-2 py-0.5 bg-green-200 rounded text-xs">
                              {a.Company?.name}
                            </span>
                          ))}
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
